import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@/fontawesome';
import { getPlanDisplayName } from '@/consts/pricingPublic';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import { formatCurrencyCentValue } from '@/utils/helpers';
import type { EuroCent, SubscriptionType } from '@/models';

interface SelectedPlansSummaryProps {
  plans: SubscriptionType[];
  /** Einzelpaket mit "Gewähltes Paket: "-Präfix anzeigen (Registrierung). Workshop-Pakete behalten den nackten Namen. */
  withPrefix?: boolean;
  /** Ein Gutschein wird im Checkout angewendet – die Summe ist dann nur der Listenpreis. */
  couponApplied?: boolean;
}

/**
 * Zeigt die gewählten Pakete eines Checkout-Links: ein Paket als Badge (wie bisher), mehrere
 * Pakete als Liste mit Einzelpreisen, Listenpreis-Summe und Hinweis auf die mehrstufige Zahlung.
 */
export default function SelectedPlansSummary({ plans, withPrefix = false, couponApplied = false }: SelectedPlansSummaryProps) {
  const { products, getProduct } = useSubscriptionProducts();

  if (plans.length <= 1) {
    const plan = plans[0];
    if (plan === undefined) {
      return null;
    }
    const name = getPlanDisplayName(products, plan);
    const label = withPrefix && getProduct(plan)?.productType !== 'WORKSHOP_01'
      ? `Gewähltes Paket: ${name}`
      : name;
    return (
      <div className="flex items-center justify-center">
        <div className="inline-flex items-start gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <FontAwesomeIcon icon={faCreditCard} className="w-3.5 h-3.5 mt-0.5 text-blue-600 dark:text-blue-400" />
          <span className="text-sm text-blue-700 dark:text-blue-300 font-medium whitespace-pre-line">{label}</span>
        </div>
      </div>
    );
  }

  const entries = plans.map(plan => {
    const product = getProduct(plan);
    const price: EuroCent | null = product !== undefined && product.stripeDataAvailable ? product.price : null;
    return { plan: plan, name: getPlanDisplayName(products, plan), price: price };
  });
  const allPricesKnown = entries.every(entry => entry.price !== null);
  const total: EuroCent = entries.reduce((sum, entry) => sum + (entry.price ?? 0), 0);

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faCreditCard} className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
        <span className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
          Gewählte Pakete
        </span>
      </div>
      <ul className="space-y-2">
        {entries.map(entry => (
          <li key={entry.plan} className="flex items-start justify-between gap-3 text-sm">
            <span className="text-blue-700 dark:text-blue-300 font-medium whitespace-pre-line">{entry.name}</span>
            {entry.price !== null && (
              <span className="text-blue-900 dark:text-blue-100 font-medium whitespace-nowrap">
                {formatCurrencyCentValue(entry.price, 'EUR')}
              </span>
            )}
          </li>
        ))}
      </ul>
      {allPricesKnown && (
        <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800 flex items-center justify-between text-sm font-semibold text-blue-900 dark:text-blue-100">
          <span>{couponApplied ? 'Gesamt vor Gutschein' : 'Gesamt'}</span>
          <span>{formatCurrencyCentValue(total, 'EUR')}</span>
        </div>
      )}
      <p className="mt-2 text-xs text-blue-700/80 dark:text-blue-300/80">
        Zahlung in {plans.length} Schritten: Nach dem ersten Checkout geht es automatisch mit dem nächsten Paket weiter.
      </p>
    </div>
  );
}
